import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormSpy } from 'react-final-form';
import { FormApi, FormState } from 'final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress, makeStyles } from '@material-ui/core';
import ErrorModal from 'components/modals/ErrorModal/ErrorModal';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import api from 'api/api';
import noop from 'utils/noop';
import { ITransactionField } from 'ui/structure/TransactionCard';
import routePaths from 'routes/routePaths';
import { WarningModal, StatusModal, OTPConfirmationModal } from 'components/modals';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { NETWORK_REQUIRED } from 'utils/constants';

import { requiredOnly, validationCallback, validateAmount, validateAccount } from 'utils/validation';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { IAccount } from 'store/accounts.model';
import { IApiResponse } from 'utils/models';
import FieldsInputsLever from 'ui/structure/FieldsInputsLever/FieldsInputsLever';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { IGetNonInstantBanks } from 'components/modules/Transfers/Transfers.model';
import { getTransferCharge, getReceipt, lookupLocalAccount, lookupOtherAccount } from './submitApi';
import {
  IFormValues,
  ITransfersState,
  IExtraFields,
  ITransferCharge,
  ITransferActions,
  types,
  IBank,
  IGetBank,
  ITransferResponse,
  bulkSortArray,
  bulkDataConfig,
  sortWithScheduleArrays,
  IAccountLookup,
  IGetBankBranches,
  TransactionType,
  ICharge,
  IGetInstitutions,
  IInstantBank,
} from './BulkManualEntry.model';
import { renderTransferFieldsFrom, renderTransferToFields } from './BulkManualEntryFields';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './BulkManualEntry.module.css';

import plus from 'assets/icons/plus.svg';
import trash from 'assets/icons/delete.png';

const warnings = {
  MAX_BULK_TRANSFERS: 'You’ve reached a maximum of 50 entries.',
  INSUFFICIENT_FUNDS: 'You have insuffient balance for this transaction',
};

const transferDataList = {
  beneficiaryName: '',
  beneficiaryBank: '',
  bankBranch: '',
  transactionAmount: '',
  transactionDescription: '',
  beneficiaryAccount: '',
  transactionType: '',
};

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  scheduleTransfer: yup.boolean(),
  frequency: yup.string().when('scheduleTransfer', {
    is: true,
    then: requiredOnly('Frequency'),
  }),
  startDate: yup.string().test('startDate', 'Special Date is required', (value, context) => {
    const FABValues: IFormValues = context.parent as IFormValues;
    if (FABValues?.frequency === 'specialDate') {
      return !!value;
    }
    return true;
  }),
  transferData: yup.array().of(
    yup.object().shape({
      transactionType: requiredOnly('Transfer Type'),
      beneficiaryName: requiredOnly('Benficiary Name'),
      beneficiaryBank: requiredOnly('Benficiary Bank'),
      bankBranch: yup.string().when('transactionType', {
        is: 'noninstant',
        then: requiredOnly('Bank branch is required'),
      }),
      charge: yup.string(),
      beneficiaryId: yup.string(),
      transactionAmount: validateAmount(),
      transactionDescription: requiredOnly('Narration'),
      beneficiaryAccount: validateAccount('Benficiary Account'),
    }),
  ),
});

interface IProps {
  accounts: IAccount[];
  bulkTransfersState: 'adding' | 'confirmation' | 'report';
  showMainForm: boolean;
  handleChangeForm: () => void;
}

const initialValues = {
  sourceAccount: '',
  transferData: [
    {
      beneficiaryName: '',
      beneficiaryBank: '',
      bankBranch: '',
      transactionAmount: '',
      transactionDescription: '',
      beneficiaryAccount: '',
      transactionType: '',
    },
  ],
};

const INITIAL_EXTRAS: IExtraFields = {
  destinationBankName: '',
  destinationBankCode: '',
  destinationAccountName: '',
  destinationIsExistingBen: false,
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    columnGap: '24px',
    margin: 0,
    paddingTop: 16,
  },
  label: {
    fontFamily: 'var(--ff-etelka-text)',
    fontSize: 14,
    lineHeight: '24px',
    '&.Mui-disabled': {
      color: '#000',
    },
  },
});

const BulkManualEntry = ({ accounts, showMainForm, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);

  const [state, setState] = useState<ITransfersState>({
    transferBeneficiaries: [],
    types,
    banks: [],
    bankBranch: {},
    extras: INITIAL_EXTRAS,
    lastLookedUpAccount: '',
    lastLookedUpBankCode: '',
  });
  const updateState = useCallback(
    (update: Partial<ITransfersState>) => setState((current) => ({ ...current, ...update })),
    [],
  );
  const updateExtras = useCallback(
    (update: Partial<IExtraFields>) =>
      setState((current) => ({ ...current, extras: { ...current.extras, ...update } })),
    [],
  );

  const [loadingData, setLoadingData] = useState(true);
  const [shouldFindInitBen, setShouldFindInitBen] = useState(false);
  const [banksMappedList, setBanksMappedList] = useState<IBank[]>([]);
  const [instantBanksMappedList, setInstantBanksMappedList] = useState<IInstantBank[]>([]);
  const [banksBranchList, setBanksBranchList] = useState({});
  const [banksList, setBanksList] = useState({});
  const [nonInstantCharge, setNonInstantCharge] = useState(0);
  // const [instantCharge, setInstantCharge] = useState(0);

  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const [toggleSchedule, setToggleSchedule] = useState<boolean | null>(null);

  const [errorModal, setErrorModal] = useState<string>();
  const closeErrorModal = () => {
    setErrorModal(undefined);
  };

  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  // const location = useLocation<ITransferBenLocationData>();
  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  useEffect(() => {
    setLoadingData(true);
    const fetchInstantBanks = () =>
      api
        .get('transfer/otherbank/institutions', {}, true, false)
        .then((data: IGetInstitutions) => {
          const instantMapper = (nBank: IInstantBank) => ({
            name: nBank.name,
            code: nBank.code,
          });
          const banks: IInstantBank[] = data.banks.map(instantMapper);

          setInstantBanksMappedList(banks);
        })
        .catch((e) => console.error(e));
    const fetchBanks = () =>
      api
        .get('reference/banks', {}, true, false)
        .then((data: IGetNonInstantBanks) => {
          const nonInstantMapper = (nBank: IGetBank): IBank => ({
            name: nBank.BankName,
            code: nBank.BankSortCode,
            shortName: nBank.BankShortName,
          });
          const newBanks: IBank[] = data.banks.map(nonInstantMapper);
          setBanksMappedList(newBanks);
        })
        .catch((e) => console.error(e));
    const fetchNonInstantCharge = () =>
      api
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        .get(`transfer/otherbank/charge?type=${TransactionType.NON_INSTANT_INTERBANK}`, {}, true, false)
        .then((data: ITransferCharge) => {
          setNonInstantCharge(data.charge);
        })
        .catch((e) => console.error(e));

    Promise.all([fetchInstantBanks(), fetchBanks(), fetchNonInstantCharge()]).finally(() => {
      setLoadingData(false);
    });
  }, []);

  const fetchbranhces = (code: string, data: string) => {
    api
      .get(`transfer/bankBranch?bankSortCode=${code}`, {}, true, true)
      .then((response: IGetBankBranches) => {
        setBanksBranchList((prev) => ({ ...prev, [`${data}`]: response?.banks }));
      })
      .catch((error: IApiResponse) => {
        console.error(error);
      });
  };

  const filterBankBranch: any = (current) => {
    const value = mainForm.getState().values;
    const { transferData } = value;
    transferData.forEach((currentTransfer, index) => {
      const data = currentTransfer.beneficiaryBank;
      if (data && current === index) {
        fetchbranhces(currentTransfer?.beneficiaryBank, data);
      }
    });
  };

  useEffect(() => {
    updateState({ banks: banksMappedList });
  }, [banksMappedList, updateState]);

  const findBeneficiaryName = useCallback(async (index: number) => {
    const value = mainForm.getState().values;
    const { sourceAccount } = value;
    const beneficiaryData = value?.transferData || [];
    const currentBen = beneficiaryData[index];
    const data = currentBen.transactionType && currentBen.beneficiaryAccount;
    if (data) {
      if (currentBen.transactionType === 'domestic') {
        try {
          const response: IAccountLookup = await lookupLocalAccount(currentBen.beneficiaryAccount);
          mainForm.mutators.changeFieldValue(`transferData[${index}].beneficiaryName`, String(response.name));
        } catch (error) {
          console.error(error);
        } finally {
          updateState({
            isLookingUp: false,
          });
        }
      }

      const instantData = currentBen.beneficiaryBank && currentBen.beneficiaryAccount;

      if (currentBen.transactionType === 'instant' && instantData) {
        try {
          const res: IAccountLookup = await lookupOtherAccount(
            currentBen.beneficiaryAccount,
            currentBen.beneficiaryBank,
            sourceAccount,
          );
          // eslint-disable-next-line max-len
          mainForm.mutators.changeFieldValue(`transferData[${index}].beneficiaryId`, res.beneficiaryId);
          mainForm.mutators.changeFieldValue(`transferData[${index}].beneficiaryName`, String(res.name));
        } catch (error) {
          console.error(error);
        } finally {
          updateState({
            isLookingUp: false,
          });
        }
      }
    }
  }, []);

  const getTransactionCharge = useCallback(async (index: number) => {
    const value = mainForm.getState().values;
    const beneficiaryData = value?.transferData || [];
    const currentTransfer = beneficiaryData[index];
    const data = currentTransfer?.transactionType && currentTransfer?.transactionAmount;
    if (data) {
      if (currentTransfer.transactionType === 'instant') {
        try {
          const response: ICharge = await getTransferCharge(currentTransfer.transactionAmount, 'INSTANT_INTERBANK');
          // eslint-disable-next-line max-len
          mainForm.mutators.changeFieldValue(`transferData[${index}].charge`, String(response.charge));
        } catch (error) {
          console.error(error);
        } finally {
          updateState({
            isLookingUp: false,
          });
        }
      }
    }
  }, []);

  const handleTypeChange = () => {
    const value = mainForm.getState().values;
    const { transferData } = value;

    transferData.forEach((currentTransfer, index) => {
      const data = currentTransfer.transactionType;
      if (data) {
        if (currentTransfer.transactionType === 'domestic') {
          const selectedBank = state.banks.find((b) => b.shortName === 'FAMBNK');

          mainForm.mutators.changeFieldValue(`transferData[${index}].beneficiaryBank`, selectedBank?.code);
          mainForm.mutators.changeFieldValue(`transferData[${index}].charge`, 0);
          setBanksList((prev) => ({ ...prev, [`${data}`]: banksMappedList }));
        }

        if (currentTransfer.transactionType === 'noninstant') {
          mainForm.mutators.changeFieldValue(`transferData[${index}].charge`, nonInstantCharge);
          setBanksList((prev) => ({ ...prev, [`${data}`]: banksMappedList }));
        }

        if (currentTransfer.transactionType === 'instant') {
          setBanksList((prev) => ({ ...prev, [`${data}`]: instantBanksMappedList }));
        }
      }
    });
  };

  const handleSelectBank = useCallback(
    (index) => {
      filterBankBranch(index);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      findBeneficiaryName(index);
    },
    [state.banks, updateExtras, updateState],
  );

  const onChangeForm = useCallback(
    ({ dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
    },
    [handleChangeForm],
  );

  const actions = useMemo<ITransferActions>(
    () => ({
      handleSelectBank,
      findBeneficiaryName,
      getTransactionCharge,
    }),
    [findBeneficiaryName, getTransactionCharge, handleSelectBank],
  );

  useEffect(() => {
    if (loadingData || !shouldFindInitBen || !mainForm) return;
    setShouldFindInitBen(false);
  }, [loadingData, shouldFindInitBen, mainForm]);

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  const onResend = () => api.post('/customer/otp/generate');

  const getPrincipalAmount = (values: IFormValues) => {
    const transferList = values.transferData;
    return transferList.reduce((total, item) => total + Number(item.transactionAmount) || 0, 0);
  };

  const getTotalCharge = (values: IFormValues) => {
    const transferList = values.transferData;
    const total = transferList.reduce((sum, item) => sum + Number(item.charge) || 0, 0);
    return total;
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const handleAddBulkTransfer = useCallback((values: IFormValues) => {
    const readyValues = {
      sourceAccount: values.sourceAccount,
      Numberoftransactions: values.transferData.length,
      PrincipalAmount: getPrincipalAmount(values),
      totalAmount: getPrincipalAmount(values) + getTotalCharge(values),
      frequency: values.frequency === 'specialDate' ? 'SPECIAL DATE' : values.frequency?.toLocaleUpperCase(),
      startDate: values.startDate || '',
      transactionCharge: getTotalCharge(values) || 0,
    } as Record<string, string | number>;

    const account = accounts.find((a) => a.accountNumber === values.sourceAccount);
    if (Number(account?.availableBalance) < Number(readyValues.totalAmount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
      return;
    }

    const newSortArrays = values.scheduleTransfer ? sortWithScheduleArrays : bulkSortArray;
    setPreviewData(getTransactionsArray(readyValues, bulkDataConfig, undefined, newSortArrays));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  }, []);

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const data = values.transferData.map((item) => ({
            transactionAmount: Number(item.transactionAmount),
            transactionNarration: item.transactionDescription,
            bankCode:
              item.transactionType === 'instant'
                ? instantBanksMappedList.find((b) => b.code === item.beneficiaryBank)?.code
                : banksMappedList.find((b) => b.code === item.beneficiaryBank)?.code,
            beneficiaryName: item.beneficiaryName,
            beneficiaryBank:
              item.transactionType === 'instant'
                ? instantBanksMappedList.find((b) => b.code === item.beneficiaryBank)?.name
                : banksMappedList.find((b) => b.code === item.beneficiaryBank)?.name,
            beneficiaryAccount: item.beneficiaryAccount,
            transactionType: item.transactionType,
            charge: item.charge,
            beneficiaryId: item.beneficiaryId,
          }));

          const requestBody = {
            sourceAccount: values.sourceAccount,
            transferData: JSON.stringify(data),
            password: otp,
            passwordType: 'OTP',
            saveBen: saveBeneficiary,
            isSchedule: values.scheduleTransfer,
          };

          api
            .post('transfer/manuelBulkTransfer', requestBody)
            .then((response: ITransferResponse) => {
              setSuccessMsg(response.description || 'Transfer Completed Successfully!');
              closeConfirmation();
              setSubmitStatus(AxiosStatus.SUCCESS);
              setTransactionId(response.transactionId);
            })
            .catch((error: IApiResponse) => {
              setErrorMsg(error.description || 'Transfer failed.\nUnable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts, state],
  );

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const subTitleItem = (
    <div>
      <p className={styles.subTitleItem}>Fill in the form below to create a list of bulk beneficiaries</p>
    </div>
  );

  const classes = useStyles();

  const dateFilterChange = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const handleClearDateInput = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const toggleScheduleTransfer = () => setToggleSchedule((prev) => !prev);

  useEffect(() => {
    if (toggleSchedule == null) {
      return;
    }

    mainForm.change('scheduleTransfer', toggleSchedule);
    if (!toggleSchedule) {
      mainForm.change('frequency', '');
      mainForm.change('startDate', '');
    }
  }, [toggleSchedule]);

  return (
    <>
      {showMainForm && (
        <DashboardCard>
          <Form
            onSubmit={handleAddBulkTransfer}
            validate={validate}
            initialValues={initialValues}
            mutators={{ changeFieldValue, setFieldTouched, ...arrayMutators }}
            render={({ handleSubmit, form, values, hasValidationErrors }) => {
              mainForm = mainForm || form;
              return (
                <form onSubmit={handleSubmit}>
                  <FieldsLeveler label="Source Account">
                    {renderTransferFieldsFrom(
                      values,
                      accounts,
                      state,
                      actions,
                      form,
                      submitting,
                      toggleSchedule,
                      toggleScheduleTransfer,
                      classes,
                      dateFilterChange,
                      handleClearDateInput,
                    )}
                  </FieldsLeveler>
                  <FieldsLeveler label="Beneficiary List" subTitle={subTitleItem}>
                    {' '}
                  </FieldsLeveler>
                  <FieldArray name="transferData">
                    {({ fields }) => (
                      <>
                        {fields.map((name: string, index: number) => (
                          <div key={name} className={styles.fields}>
                            <div className={styles.fieldsContainer}>
                              <FieldsInputsLever>
                                {renderTransferToFields(
                                  name,
                                  values,
                                  accounts,
                                  state,
                                  actions,
                                  form,
                                  submitting,
                                  banksBranchList,
                                  index,
                                  handleTypeChange,
                                  banksList,
                                )}
                              </FieldsInputsLever>
                            </div>
                            {fields.length && fields.length > 1 ? (
                              <div
                                className={styles.headerButton}
                                role="button"
                                tabIndex={0}
                                onClick={() => fields.remove(index)}
                                onKeyPress={noop}
                              >
                                <img src={trash} alt="delete icon" width="40" height="40" />
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </>
                    )}
                  </FieldArray>
                  {values.transferData.length === 50 ? (
                    <p>{warnings.MAX_BULK_TRANSFERS}</p>
                  ) : (
                    <div
                      className={styles.addBeneficiaryButton}
                      role="button"
                      tabIndex={0}
                      onKeyPress={noop}
                      onClick={() => form.mutators.push('transferData', transferDataList)}
                    >
                      <img src={plus} alt="delete icon" />
                      <p>Add one more beneficiary</p>
                    </div>
                  )}
                  <SubmitLeveler>
                    <Button type="submit" disabled={submitting || hasValidationErrors} fullWidth>
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Make Transfer'}
                    </Button>
                  </SubmitLeveler>
                  <OTPConfirmationModal
                    open={isConfirmationOpen}
                    data={previewData}
                    onClose={closeConfirmation}
                    onResend={onResend}
                    OTPPinError={OTPError}
                    onSubmit={onConfirm(values)}
                    submitStatus={submitStatus}
                    onSuccess={handleOnSuccess}
                    onRetry={onRetryConfirm}
                    getReceipt={onGetReceipt}
                    successText={successMsg}
                    failureText={errorMsg}
                    {...(state.extras.destinationIsExistingBen
                      ? { disableSaveBeneficiary: true, defaultSaveBeneficiary: false }
                      : {})}
                  />
                  <StatusModal
                    open={showStatusModal}
                    status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                    onClose={onCloseStatusModal}
                    onRetry={onCloseStatusModal}
                    onRetryText="Close"
                  >
                    {submitStatus === AxiosStatus.SUCCESS ? (
                      successMsg
                    ) : (
                      <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
                    )}
                  </StatusModal>
                  <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
                  <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
                    {simpleWarning}
                  </WarningModal>
                  <ErrorModal open={!!errorModal} onClose={closeErrorModal}>
                    {errorModal}
                  </ErrorModal>
                </form>
              );
            }}
          />
        </DashboardCard>
      )}
    </>
  );
};

export default BulkManualEntry;
